/**
 * React Query hooks for Delegation Tasks
 *
 * Provides data fetching for delegation task monitoring,
 * polling, and cancellation.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/lib/api-client';
import { useTenant } from '@/contexts/TenantContext';
import type {
  DelegationTask,
  DelegationTaskResult,
  DelegationTaskListResponse,
  DelegationTaskStatus,
} from '../types';

const DELEGATION_TASKS_KEY = 'delegation-tasks';

// =============================================================================
// Queries
// =============================================================================

/**
 * Poll a single delegation task status
 */
export function useDelegationTask(
  taskId: string | undefined,
  options?: { refetchInterval?: number | false }
) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: [DELEGATION_TASKS_KEY, tenantId, taskId],
    queryFn: async () => {
      if (!tenantId) throw new Error('No tenant selected');
      const { data } = await apiClient.get<DelegationTask>(
        `/api/v1/tenants/${tenantId}/delegations/${taskId}`,
      );
      return data;
    },
    enabled: !!taskId && !!tenantId,
    refetchInterval: options?.refetchInterval ?? false,
  });
}

/**
 * Get delegation task result (only when terminal status)
 */
export function useDelegationTaskResult(
  taskId: string | undefined,
  options?: { enabled?: boolean }
) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: [DELEGATION_TASKS_KEY, tenantId, taskId, 'result'],
    queryFn: async () => {
      if (!tenantId) throw new Error('No tenant selected');
      const { data } = await apiClient.get<DelegationTaskResult>(
        `/api/v1/tenants/${tenantId}/delegations/${taskId}/result`,
      );
      return data;
    },
    enabled: (options?.enabled ?? true) && !!taskId && !!tenantId,
  });
}

/**
 * List delegation tasks for a conversation (with optional filtering)
 */
export function useDelegationTasks(
  conversationId: string | undefined,
  options?: { status?: DelegationTaskStatus; limit?: number; offset?: number }
) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  const params = new URLSearchParams();
  if (options?.status) params.set('status', options.status);
  if (options?.limit) params.set('limit', String(options.limit));
  if (options?.offset) params.set('offset', String(options.offset));
  const qs = params.toString();
  const path = `/api/v1/tenants/${tenantId}/conversations/${conversationId}/delegations${qs ? `?${qs}` : ''}`;

  return useQuery({
    queryKey: [DELEGATION_TASKS_KEY, tenantId, 'conversation', conversationId, options],
    queryFn: async () => {
      if (!tenantId) throw new Error('No tenant selected');
      const { data } = await apiClient.get<DelegationTaskListResponse>(path);
      return data;
    },
    enabled: !!conversationId && !!tenantId,
  });
}

// =============================================================================
// Mutations
// =============================================================================

/**
 * Cancel a running delegation task
 */
export function useCancelDelegationTask() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useMutation({
    mutationFn: async (taskId: string) => {
      if (!tenantId) throw new Error('No tenant selected');
      await apiClient.delete(`/api/v1/tenants/${tenantId}/delegations/${taskId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [DELEGATION_TASKS_KEY] });
    },
  });
}
